// En lille tween-motor: tal på almindelige objekter, et par easing-kurver og ingen afhængigheder.

export type Ease = (t: number) => number;

export const ease = {
  linear: (t: number) => t,
  outQuad: (t: number) => 1 - (1 - t) * (1 - t),
  inQuad: (t: number) => t * t,
  inOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  outCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  outBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
  outElastic: (t: number) => {
    if (t === 0 || t === 1) return t;
    return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1;
  },
} satisfies Record<string, Ease>;

interface Tween {
  target: Record<string, number>;
  from: Record<string, number>;
  to: Record<string, number>;
  t: number;
  dur: number;
  delay: number;
  ease: Ease;
  done?: () => void;
}

export class Tweens {
  private list: Tween[] = [];

  /** Tween tal-felter på `target` til `to` over `ms` millisekunder. */
  to<T extends object>(target: T, to: Partial<Record<keyof T, number>>, ms: number, e: Ease = ease.outQuad, done?: () => void, delay = 0): void {
    const t = target as unknown as Record<string, number>;
    const from: Record<string, number> = {};
    const goal: Record<string, number> = {};
    for (const [k, v] of Object.entries(to)) {
      if (typeof v !== 'number') continue;
      from[k] = t[k] ?? 0;
      goal[k] = v;
    }
    // En ny tween på samme felt afløser den gamle.
    this.list = this.list.filter((x) => x.target !== t || !Object.keys(goal).some((k) => k in x.to));
    const tw: Tween = { target: t, from, to: goal, t: 0, dur: Math.max(1, ms), delay, ease: e };
    if (done) tw.done = done;
    this.list.push(tw);
  }

  /** Gå fra `from` til `to` (sætter startværdien først). */
  fromTo<T extends object>(target: T, from: Partial<Record<keyof T, number>>, to: Partial<Record<keyof T, number>>, ms: number, e: Ease = ease.outQuad, done?: () => void, delay = 0): void {
    Object.assign(target, from);
    this.to(target, to, ms, e, done, delay);
  }

  update(dtMs: number): void {
    if (this.list.length === 0) return;
    const finished: Tween[] = [];
    for (const tw of this.list) {
      if (tw.delay > 0) {
        tw.delay -= dtMs;
        if (tw.delay > 0) continue;
      }
      tw.t = Math.min(tw.dur, tw.t + dtMs);
      const k = tw.ease(tw.t / tw.dur);
      for (const key of Object.keys(tw.to)) {
        const a = tw.from[key] ?? 0;
        const b = tw.to[key] ?? 0;
        tw.target[key] = a + (b - a) * k;
      }
      if (tw.t >= tw.dur) finished.push(tw);
    }
    if (finished.length === 0) return;
    this.list = this.list.filter((x) => !finished.includes(x));
    for (const tw of finished) tw.done?.();
  }

  clear(): void {
    this.list = [];
  }

  get size(): number {
    return this.list.length;
  }
}
